<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import PortalSidebar from '@/components/PortalSidebar.vue'
import { signInHref, signOutHref } from '@/lib/links'
import { type AppLink, type CompanyContext, readSessionStatus, selectContext } from '@/lib/session'

const props = withDefaults(defineProps<{
  appBase: string
  brandName: string
  logoUrl: string
  returnUrl: string | null
  statusEndpoint: string
  contextEndpoint: string
  logoutUrl: string
  navigate?: (url: string) => void
}>(), {
  navigate: (url: string) => { window.location.assign(url) },
})

const OPEN_ERROR = "We couldn't open that company. Try again."
const LOAD_ERROR = "We couldn't load your companies. Try again."
const LOCKED_ERROR = 'You can no longer act for that company. Ask your administrator.'

type View = 'loading' | 'signed-in' | 'signed-out' | 'error'

const view = ref<View>('loading')
const user = ref<{ displayName: string, email: string } | null>(null)
const contexts = ref<CompanyContext[]>([])
const filter = ref('')
const busy = ref(false)
const error = ref<string | null>(null)

const rows = computed(() => {
  const term = filter.value.trim().toLowerCase()
  if (term === '') {
    return contexts.value
  }
  return contexts.value.filter(c =>
    c.name.toLowerCase().includes(term) || c.partyRoleId.toLowerCase().includes(term))
})

const signIn = computed(() => signInHref(props.appBase, props.returnUrl))
const signOut = computed(() => signOutHref(props.logoutUrl, props.appBase))

onMounted(async () => {
  const status = await readSessionStatus(props.statusEndpoint)

  if (status.kind === 'signed-out') {
    view.value = 'signed-out'
    return
  }
  if (status.kind === 'error') {
    view.value = 'error'
    return
  }

  user.value = { displayName: status.displayName, email: status.email }
  contexts.value = status.contexts
  view.value = 'signed-in'
})

async function open(context: CompanyContext, link: AppLink) {
  if (busy.value) {
    return
  }
  busy.value = true
  error.value = null

  const result = await selectContext({
    endpoint: props.contextEndpoint,
    partyRoleId: context.partyRoleId,
    returnUrl: props.returnUrl,
    app: link.path,
  })

  if (result.kind === 'redirect') {
    props.navigate(result.redirectUrl)
    return
  }

  // A lapsed session signs in again; a refusal never retries.
  if (result.kind === 'signed-out') {
    view.value = 'signed-out'
    busy.value = false
    return
  }

  error.value = result.kind === 'not-authorized' ? LOCKED_ERROR : OPEN_ERROR
  busy.value = false
}
</script>

<template>
  <div
    class="picker"
    data-testid="company-picker"
  >
    <template v-if="view === 'signed-in'">
      <PortalSidebar
        :logo-url="logoUrl"
        :brand-name="brandName"
        context="Choose a company"
        :user="user"
        :sign-out-href="signOut"
      />
      <main class="picker__main">
        <h1 class="picker__title">
          Which company?
        </h1>
        <input
          v-model="filter"
          class="login-form__input picker__filter"
          type="text"
          placeholder="Filter by name or VPAY id"
          aria-label="Filter by name or VPAY id"
        >
        <p
          v-if="contexts.length === 0"
          class="picker__empty"
          data-testid="no-companies"
        >
          No company is set up for you yet — ask your administrator
        </p>
        <ul
          v-else
          class="picker__rows"
        >
          <li
            v-for="context in rows"
            :key="context.partyRoleId"
            data-testid="company-row"
            :data-party-role-id="context.partyRoleId"
            :data-party-user-id="context.partyUserId"
          >
            <!-- One app: the row opens it. -->
            <button
              v-if="context.appLinks.length === 1"
              type="button"
              class="picker__row"
              data-testid="app-link"
              :data-app-path="context.appLinks[0].path"
              :disabled="busy"
              @click="open(context, context.appLinks[0])"
            >
              <span class="picker__who">
                <span
                  class="picker__name"
                  data-testid="company-name"
                >{{ context.name }}</span>
                <span class="picker__id">{{ context.partyRoleId }}</span>
              </span>
              <span
                v-if="context.selected"
                class="picker__badge"
              >Current</span>
              <span class="picker__single">Open {{ context.appLinks[0].label }}</span>
            </button>
            <!-- Several apps: one launch chip each. -->
            <div
              v-else-if="context.appLinks.length > 1"
              class="picker__row picker__row--multi"
            >
              <span class="picker__who">
                <span
                  class="picker__name"
                  data-testid="company-name"
                >{{ context.name }}</span>
                <span class="picker__id">{{ context.partyRoleId }}</span>
              </span>
              <span
                v-if="context.selected"
                class="picker__badge"
              >Current</span>
              <span class="picker__apps">
                <button
                  v-for="link in context.appLinks"
                  :key="link.path"
                  type="button"
                  class="picker__app"
                  data-testid="app-link"
                  :data-app-path="link.path"
                  :disabled="busy"
                  @click="open(context, link)"
                >{{ link.label }}</button>
              </span>
            </div>
            <!-- No apps: listed, but nothing to open. -->
            <div
              v-else
              class="picker__row picker__row--none"
            >
              <span class="picker__who">
                <span
                  class="picker__name"
                  data-testid="company-name"
                >{{ context.name }}</span>
                <span class="picker__id">{{ context.partyRoleId }}</span>
              </span>
              <span
                v-if="context.selected"
                class="picker__badge"
              >Current</span>
              <span class="picker__none">No apps set up yet — ask your administrator</span>
            </div>
          </li>
          <li
            v-if="rows.length === 0"
            class="picker__empty"
            data-testid="no-match"
          >
            No company matches.
          </li>
        </ul>
        <p
          v-if="error"
          class="login-form__error"
          data-testid="error"
          role="alert"
        >
          {{ error }}
        </p>
      </main>
    </template>

    <main
      v-else
      class="shell"
    >
      <section class="shell__panel">
        <img
          class="shell__logo"
          :src="logoUrl"
          :alt="brandName"
        >
        <template v-if="view === 'loading'">
          <p
            class="shell__lede"
            data-testid="loading"
          >
            Loading your companies…
          </p>
        </template>
        <template v-else-if="view === 'signed-out'">
          <h1 class="shell__title">
            Your session has ended
          </h1>
          <p class="shell__lede">
            Sign in again to choose a company.
          </p>
          <a
            class="login-form__submit picker__link"
            data-testid="sign-in-link"
            :href="signIn"
          >Sign in</a>
        </template>
        <template v-else>
          <h1 class="shell__title">
            Something went wrong
          </h1>
          <p
            class="login-form__error"
            data-testid="error"
            role="alert"
          >
            {{ LOAD_ERROR }}
          </p>
        </template>
      </section>
    </main>
  </div>
</template>

<style scoped>
.picker {
  display: flex;
  min-height: 100dvh;
}

.picker__main {
  flex: 1;
  max-width: 48rem;
  padding: 3rem 2.5rem 5rem;
}

.picker__title {
  margin: 0 0 1rem;
  font-size: 1.75rem;
}

.picker__filter {
  width: 100%;
  margin-bottom: 1rem;
}

.picker__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border);
}

.picker__row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  align-items: center;
  padding: 1rem 0.5rem;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.picker__row:hover,
.picker__row:focus-visible {
  background: var(--background);
  outline: none;
}

.picker__row--multi,
.picker__row--none {
  cursor: default;
}

.picker__row--none {
  opacity: 0.6;
}

.picker__who {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}

.picker__name {
  font-weight: 600;
}

.picker__id {
  color: var(--muted-foreground);
  font-family: ui-monospace, monospace;
  font-size: 0.8125rem;
}

.picker__badge {
  padding: 0.125rem 0.5rem;
  border: 1px solid var(--primary);
  border-radius: 999px;
  color: var(--primary);
  font-size: 0.75rem;
  font-weight: 600;
}

.picker__single,
.picker__none {
  color: var(--muted-foreground);
  font-size: 0.875rem;
}

.picker__apps {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.picker__app {
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--card);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
  cursor: pointer;
}

.picker__app:hover,
.picker__app:focus-visible {
  border-color: var(--primary);
  color: var(--primary);
  outline: none;
}

.picker__empty {
  padding: 1rem 0.5rem;
  color: var(--muted-foreground);
}

.picker__link {
  display: inline-block;
  text-decoration: none;
  text-align: center;
}

@media (max-width: 48rem) {
  .picker {
    flex-direction: column;
  }

  .picker__main {
    padding: 1.5rem 1rem 4rem;
  }
}
</style>
